import { useDeck } from "../../../lib/deck-context";
import { BASE, fmtM, fmtPct, fmtUsd } from "../../../lib/model";
import { Card, Eyebrow, Note } from "../../deck/primitives";
import { SCENARIOS, TENANT_CATS, TRADE, TRADE_SRC, TRAFFIC, mixTotals } from "./data";
import { ConfirmAction, NumIn, Src, Tag } from "./ui";

const UW = [
  ["Overall occupancy", "Murrieta ~95.4%", "occ", "Conservative vs current market"],
  ["General retail rent", "~$30.62/SF 2–4 Star", "Category specific", "Validate"],
  ["Weighted project rent", "—", "rent", "Target"],
  ["Food / QSR occupancy", "No separate public series", "95%", "Underwriting assumption"],
  ["Retail occupancy", "~95% local market", "94–95%", "Underwriting"],
  ["Entertainment occupancy", "No separate public series", "90–92%", "Underwriting"],
  ["Other revenue", "Project-specific", "other", "Preliminary"],
  ["Stabilization", "Market dependent", "occ", "Preliminary"],
];

const ModelBar = () => {
  const { m, inputs, applyInputs } = useDeck();
  return (
    <Card data-testid="mk-model-bar" className="p-7 bg-ink-deep border-ink-deep text-white">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] tracking-[0.2em] text-arq-mid">UNDERWRITING INTEGRATION · LIVE MODEL</div>
          <div className="mt-2 text-sm text-white/70">Rent ${inputs.rent.toFixed(2)} · Occ {inputs.occ.toFixed(1)}% · Cap {fmtPct(inputs.cap)}</div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-5 text-right">
          {[["NOI", fmtM(m.noi, 3)], ["Value", fmtM(m.value)], ["Debt", fmtM(m.debt)], ["Build + Sell", fmtM(m.profit)], ["Refi cash-out", fmtM(m.cashOut)], ["Pad-ready exit", fmtM(m.stages.pad.created)]].map(([l, v]) => (
            <div key={l}><div className="text-[10px] text-white/50 uppercase tracking-[0.12em]">{l}</div><div data-testid={`mk-model-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="font-display text-xl tabular-nums">{v}</div></div>
          ))}
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-2 [&_button]:!text-ink">
        {Object.entries(SCENARIOS).map(([k, s]) => (
          <ConfirmAction key={k} testid={`mk-run-${k}`} label={`Run ${s.label} Case`} prompt={`Apply ${s.label.toLowerCase()} rent $${s.patch.rent} / occ ${s.patch.occ}% / cap ${s.patch.cap}% to the session model?`}
            onConfirm={() => applyInputs(k === "base" ? { rent: BASE.rent, occ: BASE.occ, cap: BASE.cap } : s.patch)} />
        ))}
      </div>
      <p className="mt-4 text-xs text-white/50">Applies to the live session only. Saved base assumptions are never overwritten — use Reset to return to the Partner Base Case.</p>
    </Card>
  );
};

const MixTable = () => {
  const { market, setMarket, applyInputs } = useDeck();
  const t = mixTotals(market.mix);
  const set = (i, k, v) => setMarket("mix", market.mix.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <Card className="overflow-x-auto">
      <div className="px-7 pt-7 pb-3 flex flex-wrap items-center justify-between gap-3">
        <div><Eyebrow>Category-Specific Underwriting</Eyebrow><div className="mt-2 font-display text-2xl text-ink">One rent and occupancy does not fit every suite</div></div>
        <Tag t="illustrative" label="Illustrative Tenant-Mix Scenario" />
      </div>
      <table data-testid="mk-mix-table" className="data-table w-full min-w-[760px]">
        <thead><tr><th>Category</th><th className="!text-right">SF</th><th className="!text-right">Rent / SF</th><th className="!text-right">Occupancy</th><th className="!text-right">Annual Revenue</th></tr></thead>
        <tbody>
          {market.mix.map((r, i) => (
            <tr key={r.id}>
              <td className="text-ink">{r.label}</td>
              <td className="text-right"><NumIn testid={`mk-mix-sf-${r.id}`} value={r.sf} step={500} onChange={(v) => set(i, "sf", v)} /></td>
              <td className="text-right"><NumIn testid={`mk-mix-rent-${r.id}`} value={r.rent} prefix="$" w="w-12" onChange={(v) => set(i, "rent", v)} /></td>
              <td className="text-right"><NumIn testid={`mk-mix-occ-${r.id}`} value={r.occ} suffix="%" w="w-12" onChange={(v) => set(i, "occ", v)} /></td>
              <td className="text-right tabular-nums text-ink">{fmtUsd(r.sf * r.rent * (r.occ / 100))}</td>
            </tr>
          ))}
          <tr className="bg-ink-deep text-white hover:!bg-ink-deep">
            <td className="font-mono text-xs tracking-[0.14em]">TOTAL / WEIGHTED</td>
            <td data-testid="mk-mix-total-sf" className="text-right tabular-nums">{t.sf.toLocaleString()}</td>
            <td data-testid="mk-mix-weighted-rent" className="text-right tabular-nums font-display text-xl text-arq-mid">${t.rent.toFixed(2)}</td>
            <td data-testid="mk-mix-weighted-occ" className="text-right tabular-nums font-display text-xl text-arq-mid">{t.occ.toFixed(1)}%</td>
            <td className="text-right tabular-nums">{fmtUsd(t.rev)}</td>
          </tr>
        </tbody>
      </table>
      <div className="px-7 py-5 flex flex-wrap gap-2 border-t border-line">
        <ConfirmAction primary testid="mk-apply-rent" label="Apply Selected Rent Scenario" prompt={`Apply ${t.sf.toLocaleString()} SF at $${t.rent.toFixed(2)}/SF and ${t.occ.toFixed(1)}% occupancy to the session model?`}
          onConfirm={() => applyInputs({ sf: t.sf, rent: Number(t.rent.toFixed(2)), occ: Number(t.occ.toFixed(1)) })} />
        <ConfirmAction testid="mk-apply-occ" label="Apply Market Occupancy to Model" prompt="Apply Murrieta 95.4% market occupancy to the session model?" onConfirm={() => applyInputs({ occ: 95.4 })} />
      </div>
    </Card>
  );
};

const TradeArea = () => (
  <Card className="p-7 lg:p-9">
    <div className="flex flex-wrap justify-between gap-3"><div><Eyebrow>Trade Area Panel</Eyebrow><div className="mt-2 font-display text-2xl text-ink">An affluent, growing immediate trade area</div></div><Tag t="market" /></div>
    <div className="mt-6 overflow-x-auto">
      <table data-testid="mk-trade-table" className="data-table w-full min-w-[560px]">
        <thead><tr><th>Metric</th><th className="!text-right">1 Mile</th><th className="!text-right">3 Miles</th><th className="!text-right">5 Miles</th></tr></thead>
        <tbody>{TRADE.map(([k, ...v]) => <tr key={k}><td className="text-ink">{k}</td>{v.map((x, i) => <td key={i} className="text-right tabular-nums">{x}</td>)}</tr>)}</tbody>
      </table>
    </div>
    <Src s={TRADE_SRC} type="Demographics (ring)" />
    <div className="mt-6 grid md:grid-cols-2 gap-4">
      {TRAFFIC.map(([k, v]) => (
        <div key={k} data-testid={`mk-traffic-${k.split(" ")[0].toLowerCase()}`} className="rounded-xl bg-paper p-5">
          <div className="text-sm text-char">{k}</div>
          <div className="mt-1 font-display text-3xl text-ink tabular-nums">{v}</div>
          <div className="text-xs text-steel">Average daily traffic · City of Murrieta / CoStar extract (Issuu)</div>
        </div>
      ))}
    </div>
    <div data-testid="mk-consumer-spending" className="mt-6 rounded-xl border border-dashed border-line p-6">
      <div className="font-display text-xl text-ink">Consumer Spending Analysis — Demographic Report Required</div>
      <div className="mt-3 space-y-1 text-sm text-steel">
        <div>Restaurant Spending — To Be Added from subject-site ESRI/CoStar report</div>
        <div>Entertainment Spending — To Be Added from subject-site ESRI/CoStar report</div>
        <div>Retail Spending — To Be Added from subject-site ESRI/CoStar report</div>
      </div>
      <p className="mt-3 text-xs text-steel">10-, 15- and 20-minute drive-time demographics will be added once a subject-site-specific CoStar / ESRI drive-time report is obtained. Reports from other Murrieta centers are intentionally not substituted.</p>
    </div>
  </Card>
);

const Score = () => {
  const { market, setMarket } = useDeck();
  const tw = market.score.reduce((t, s) => t + s.weight, 0);
  const total = tw ? market.score.reduce((t, s) => t + (s.weight * s.rating) / 10, 0) / tw * 100 : 0;
  const set = (i, k, v) => setMarket("score", market.score.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <Card className="p-7 lg:p-9">
      <div className="flex flex-wrap justify-between gap-3"><div><Eyebrow>Market Support Score</Eyebrow><div className="mt-2 text-sm text-steel">Not an external institutional rating.</div></div><Tag t="internal" /></div>
      <div className="mt-6 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-3 text-center">
          <div data-testid="mk-score-total" className="font-display text-7xl text-arq tabular-nums">{Math.round(total)}</div>
          <div className="text-xs text-steel">out of 100</div>
        </div>
        <div className="lg:col-span-9 space-y-3">
          {market.score.map((s, i) => (
            <div key={s.label} className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[220px_1fr_auto_auto] items-center gap-3 text-sm">
              <span className="text-char">{s.label}</span>
              <div className="hidden sm:block h-2 rounded-full bg-paper overflow-hidden"><div className="h-full bg-arq rounded-full" style={{ width: `${s.rating * 10}%` }} /></div>
              <span className="flex items-center gap-1 text-xs text-steel">Weight <NumIn testid={`mk-score-weight-${i}`} value={s.weight} w="w-10" onChange={(v) => set(i, "weight", v)} /></span>
              <span className="flex items-center gap-1 text-xs text-steel">Rating <NumIn testid={`mk-score-rating-${i}`} value={s.rating} w="w-10" suffix="/10" onChange={(v) => set(i, "rating", Math.min(Math.max(v, 0), 10))} /></span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export const TabUnderwriting = () => {
  const { inputs } = useDeck();
  const val = (k) => (k === "occ" ? `${inputs.occ}%` : k === "rent" ? `$${inputs.rent}/SF` : k === "other" ? `$${Math.round(inputs.other / 1000)}K/year` : k);
  return (
    <div className="space-y-6">
      <ModelBar />
      <Card className="overflow-x-auto">
        <div className="px-7 pt-7 pb-3"><Eyebrow>Market Evidence → Arqitek Underwriting</Eyebrow></div>
        <table data-testid="mk-uw-table" className="data-table w-full min-w-[720px]">
          <thead><tr><th>Metric</th><th>Market Evidence</th><th>Arqitek Underwriting</th><th>Status</th></tr></thead>
          <tbody>{UW.map(([a, b, c, d]) => <tr key={a}><td className="text-ink">{a}</td><td>{b}</td><td className="text-ink font-medium tabular-nums">{val(c)}</td><td className="text-steel">{d}</td></tr>)}</tbody>
        </table>
      </Card>
      <MixTable />
      <Card className="p-7">
        <div className="flex flex-wrap justify-between gap-3"><Eyebrow>Potential Tenant Categories</Eyebrow><Tag t="illustrative" label="Tenant-Type Examples — No Relationship Implied" /></div>
        <div className="mt-5 grid md:grid-cols-3 gap-6">
          {Object.entries(TENANT_CATS).map(([g, items]) => (
            <div key={g}><div className="font-display text-xl text-ink">{g}</div><div className="mt-3 flex flex-wrap gap-2">{items.map((t) => <span key={t} className="rounded-full bg-paper border border-line px-3 py-1 text-xs text-char">{t}</span>)}</div></div>
          ))}
        </div>
      </Card>
      <TradeArea />
      <Score />
      <Note>For QSR and entertainment, assumptions are based on rent comparables, tenant leasing activity, regional transactions and project underwriting — not a published standalone occupancy series.</Note>
    </div>
  );
};
